import { createActor, canisterId } from "./declarations/backend"
import { queryReady } from "./query_ready.js"
import { backendOrigin, NETWORK } from "./story_link.js"

// The HTTP Challenge Ports' panels. Each step (from the port's <name>.http.yml)
// calls http_request (the query) or http_request_update over Candid, as the IC's
// HTTP gateway would and as the builder's expected.txt does, with the path under
// the port's Mount, /challenges/<name>/. No CORS is involved. A GET answered by
// the query also links to the same path through the real gateway.

const encoder = new TextEncoder()
const decoder = new TextDecoder()

// The gateway's own certification headers are not part of what a port replies.
const own = (headers) => headers.filter(([name]) => !name.toLowerCase().startsWith("ic-certificate"))

export const requestFor = (step, mount) => ({
  method: step.method,
  url: `/challenges/${mount}${step.path}`,
  headers: step.headers,
  body: encoder.encode(step.body),
  ...(step.call === "http_request" && {
    certificate_version: step.certificate_version == null ? [] : [step.certificate_version],
  }),
})

export const shown = (step) => {
  if (step.fault) return `fault: ${step.fault}`
  if (step.upgrade) return `${step.status}, upgrade to an update`
  return `${step.status} "${step.reply}"${step.stateful ? " on a fresh canister" : ""}`
}

export const linkFor = (step, mount, origin) =>
  origin && step.call === "http_request" && step.method === "GET" && !step.upgrade && !step.fault
    ? `${origin}/challenges/${mount}${step.path}` : null

// { ok, text } for one reply. A stateful step's body depends on every visitor
// before you, so it passes on its status and shows the body it got.
export const judge = (step, reply) => {
  const status = Number(reply.status_code)
  const upgrade = reply.upgrade?.[0] === true
  if (step.fault) return { ok: false, text: "replied, but a fault was expected" }
  if (step.upgrade) {
    const ok = upgrade && status === step.status
    return { ok, text: ok ? "✓ upgraded" : `✗ ${status}, ${upgrade ? "upgraded" : "not upgraded"}` }
  }
  const body = decoder.decode(reply.body)
  const headersOk = !step.headers_out || JSON.stringify(own(reply.headers)) === JSON.stringify(step.headers_out)
  if (step.stateful) {
    const ok = status === step.status && !upgrade
    const note = body === step.reply ? "" : " (shared state)"
    return { ok, text: `${ok ? "✓" : "✗"} ${status} "${body}"${note}` }
  }
  const ok = status === step.status && body === step.reply && headersOk && !upgrade
  return { ok, text: ok ? "✓" : `✗ ${status} "${body}"${headersOk ? "" : ", headers differ"}` }
}

export const runStep = async (step, mount, actor) => {
  try {
    return judge(step, await actor[step.call](requestFor(step, mount)))
  } catch (err) {
    const message = String(err?.message ?? err)
    if (step.fault) {
      const ok = message.includes(`fault: ${step.fault}`)
      return { ok, text: ok ? "✓ faulted as expected" : "✗ faulted with a different message" }
    }
    return { ok: false, text: `Failed: ${message.split("\n")[0]}` }
  }
}

export const setUpPanel = (panel, id = canisterId, network = NETWORK) => {
  const mount = panel.dataset.mount
  const steps = JSON.parse(panel.querySelector("script[type='application/json']").textContent)
  const origin = backendOrigin(id, network)
  const body = panel.querySelector("tbody")
  const summary = panel.querySelector(".cases-summary")
  const cells = steps.map((step) => {
    const tr = body.insertRow()
    tr.insertCell().textContent = step.title
    const request = tr.insertCell()
    const code = document.createElement("code")
    code.textContent = `${step.call === "http_request" ? "query" : "update"} ${step.method} ${step.path}`
    request.append(code)
    const href = linkFor(step, mount, origin)
    if (href) {
      const a = document.createElement("a")
      a.href = href
      a.textContent = "open"
      a.target = "_blank"
      a.rel = "noopener"
      request.append(" ", a)
    }
    tr.insertCell().textContent = shown(step)
    return tr.insertCell()
  })

  panel.querySelector(".cases-run").addEventListener("click", async () => {
    if (!id) { summary.textContent = "No backend canister id: deploy first."; return }
    if (id === "dev-stub") { summary.textContent = "Needs a deployed canister (stubbed, no canister)."; return }
    const actor = await queryReady(createActor(id))
    let passed = 0
    cells.forEach((cell) => { cell.textContent = "…" })
    for (const [i, step] of steps.entries()) {
      const result = await runStep(step, mount, actor)
      cells[i].textContent = result.text
      if (result.ok) passed += 1
      summary.textContent = `${passed} of ${steps.length} as expected`
    }
  })
}

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll(".feature-http").forEach((panel) => setUpPanel(panel))
})
